<template>
  <div v-if="videoRoomMaster && editVideoRoom" class="w-100 d-flex flex-column">
    <proa-heading>{{ $t('pages.meeting.reservations.show.editVideoRoom') }}</proa-heading>
    <proa-card
      v-if="this.editVideoRoom"
      :class="['edit-video-room-card', {
        'disabled': updateVideoRoomRequesting
      }]"
    >
      <template v-slot:header>
        <div class="d-flex justify-content-between align-items-center">
          <div/>
          <proa-heading detail class="room-name">
            <span>
              <img :src="require('@/assets/images/icons/meeting_room.png')" />
              <span>{{ videoRoomMaster.roomName }}</span>
            </span>
          </proa-heading>
          <div>
            <span class="prefix-max">{{ $t('common.max') }}</span>
            <span>{{ videoRoomMaster.roomCapacity + $t('common.person') }}</span>
          </div>
        </div>
      </template>
      <table class="table-attributes">
        <tbody>
          <tr>
            <th>{{ $t('models.videoRoom.reservationAt') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editReservationAtModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span v-if="!editVideoRoom.startAt || !editVideoRoom.endAt" class="attribute empty">
                  {{ $t('models.videoRoom.placeHolders.reservationAt') }}
                </span>
                <span v-else class="attribute">
                  <span class="mr-2">{{ editVideoRoom.startAt | moment('YYYY年MM月DD日（ddd）') }}</span>
                  <span>{{ editVideoRoom.startAt | moment('HH:mm') }}〜{{ editVideoRoom.endAt | moment('HH:mm') }}</span>
                </span>
              </span>
            </td>
          </tr>
          <tr>
            <th>{{ $t('models.videoRoom.attributes.meetingTitle') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editMeetingTitleModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span :class="['attribute', { 'empty': !editVideoRoom.meetingTitle }]">{{ editVideoRoom.meetingTitle || $t('models.videoRoom.placeHolders.meetingTitle') }}</span>
              </span>
            </td>
          </tr>
          <tr>
            <th valign="top">{{ $t('models.videoRoom.attributes.meetingDescription') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editMeetingDescriptionModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span
                  v-html="editVideoRoom.meetingDescription ? $options.filters.autoLink(editVideoRoom.meetingDescription) : $t('models.videoRoom.placeHolders.meetingDescription')"
                  :class="['attribute text-pre-wrap', { 'empty': !editVideoRoom.meetingDescription }]"
                />
              </span>
            </td>
          </tr>
          <tr>
            <th>{{ $t('models.videoRoom.attributes.roomPassword') }}</th>
            <td>
              <span
                class="attribute-wrapper"
                @click="editRoomPasswordModal = true"
              >
                <img :src="require('@/assets/images/icons/edit.png')"/>
                <span :class="['attribute', { 'empty': !editVideoRoom.roomPassword }]">{{ editVideoRoom.roomPassword || $t('models.videoRoom.placeHolders.roomPassword') }}</span>
              </span>
            </td>
          </tr>
          <tr>
            <th>{{ $t('models.videoRoom.users') }}（{{ editVideoRoom.userIds.length }}{{ $t('common.person') }}）</th>
            <td>
              <proa-button
                @click.native="addVideoRoomMembersModal = true"
                class="default-button-width mr-2"
              >
                {{ $t('common.addMember') }}
              </proa-button>
              <proa-button
                :disabled="editVideoRoom.userIds.length == 1"
                @click.native="deleteVideoRoomMembersModal = true"
                class="default-button-width"
              >
                {{ $t('common.deleteMember') }}
              </proa-button>
            </td>
          </tr>
        </tbody>
      </table>
      <div class="members">
        <div class="members-scroll-wrapper">
          <div v-for="user in addedUsers" :key="user.id" class="member">
            <proa-avatar :src="user.meta.avatar || require('@/assets/images/avatar_default.png')" size="sm"/>
            <span class="ml-2 pl-1">{{ user.meta.displayName }}</span>
          </div>
          <div v-for="guestUser in editVideoRoom.guestUsers" :key="guestUser.email" class="member">
            <proa-avatar :src="require('@/assets/images/avatar_default.png')" size="sm" guest/>
            <span class="ml-2 pl-1">
              <span>{{ guestUser.lastName }} </span>
              <span class="text-secondary">{{ guestUser.firstName }}</span>
              <span> / {{ guestUser.email }}</span>
            </span>
          </div>
        </div>
      </div>
      <div class="edit-video-room-card-footer">
        <proa-button
           @click.native="onClickCancel"
           class="mr-2 default-button-width"
         >
           {{ $t('common.cancel') }}
         </proa-button>
         <proa-button
           :disabled="!validated"
           :loading="updateVideoRoomRequesting"
           variant="primary"
           class="default-button-width"
           @click.native="onClickUpdateVideoRoom"
         >
           {{ $t('common.update') }}
         </proa-button>
      </div>
    </proa-card>
    <validation-observer v-slot="{ invalid }">
      <proa-modal
        :modal="editMeetingTitleModal"
        :title="$t('models.videoRoom.attributes.meetingTitle')"
        @close="closeEditMeetingTitleModal(false)"
      >
        <validation-provider
          :name="$t('models.videoRoom.attributes.meetingTitle')"
          rules="max:50"
          v-slot="{ errors }"
        >
          <proa-input-group :invalidFeedback="errors[0]">
            <template slot="input">
              <proa-input
                v-model="editVideoRoomInner.meetingTitle"
                :invalid="errors.length != 0"
                type="text"
                dark
              />
            </template>
          </proa-input-group>
        </validation-provider>
        <template slot="footer">
          <proa-button
            @click.native="closeEditMeetingTitleModal(false)"
            class="default-button-width mr-2"
          >
            {{ $t('common.cancel') }}
          </proa-button>
          <proa-button
            @click.native="closeEditMeetingTitleModal(true)"
            :disabled="invalid"
            class="default-button-width"
          >
            {{ $t('common.ok') }}
          </proa-button>
        </template>
      </proa-modal>
    </validation-observer>
    <proa-modal
      :modal="editMeetingDescriptionModal"
      :title="$t('models.videoRoom.attributes.meetingDescription')"
      @close="closeEditMeetingDescriptionModal(false)"
    >
      <proa-input
        v-model="editVideoRoomInner.meetingDescription"
        type="textarea"
        dark
      />
      <template slot="footer">
        <proa-button @click.native="closeEditMeetingDescriptionModal(false)" class="default-button-width mr-2">
          {{ $t('common.cancel') }}
        </proa-button>
        <proa-button
          class="default-button-width"
          @click.native="closeEditMeetingDescriptionModal(true)"
        >
          {{ $t('common.ok') }}
        </proa-button>
      </template>
    </proa-modal>
    <validation-observer v-slot="{ invalid }">
      <proa-modal
        :modal="editRoomPasswordModal"
        :title="$t('models.videoRoom.attributes.roomPassword')"
        @close="closeEditRoomPasswordModal(false)"
      >
        <validation-provider
          :name="$t('models.videoRoom.attributes.roomPassword')"
          rules="max:50"
          v-slot="{ errors }"
        >
          <proa-input-group :invalidFeedback="errors[0]">
            <template slot="input">
              <proa-input
                v-model="editVideoRoomInner.roomPassword"
                :invalid="errors.length != 0"
                type="text"
                dark
              />
            </template>
          </proa-input-group>
        </validation-provider>
        <template slot="footer">
          <proa-button
            @click.native="closeEditRoomPasswordModal(false)"
            class="default-button-width mr-2"
          >
            {{ $t('common.cancel') }}
          </proa-button>
          <proa-button
            @click.native="closeEditRoomPasswordModal(true)"
            :disabled="invalid"
            class="default-button-width"
          >
            {{ $t('common.ok') }}
          </proa-button>
        </template>
      </proa-modal>
    </validation-observer>
    <select-reservation-at-by-room-modal
      :modal="editReservationAtModal"
      :videoRoomMaster="videoRoomMaster"
      :startAt="editVideoRoom.startAt"
      :endAt="editVideoRoom.endAt"
      :reservedRoomId="String(videoRoomId)"
      @close="editReservationAtModal = false"
      @confirm="selectedReservationAt"
    />
    <select-member-modal
      :modal="addVideoRoomMembersModal"
      :userIds="editVideoRoom.userIds"
      :guestMembers="editVideoRoom.guestUsers"
      :upperLimit="videoRoomMaster.roomCapacity - editVideoRoom.userIds.length"
      type="add"
      guestable
      @close="addVideoRoomMembersModal = false"
      @confirm="addUsers"
    />
    <select-member-modal
      :modal="deleteVideoRoomMembersModal"
      :userIds="editVideoRoom.userIds"
      :guestMembers="editVideoRoom.guestUsers"
      type="delete"
      guestable
      @close="deleteVideoRoomMembersModal = false"
      @confirm="deleteUsers"
    />
    <proa-modal
      :modal="updatedModal"
      :title="$t('models.videoRoom.updated')"
      @close="closeUpdatedModal"
    >
      {{ $t('pages.meeting.reservations.edit.updatedMessage') }}
    </proa-modal>
  </div>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

import User from '@/store/models/User'
import VideoRoom from '@/store/models/VideoRoom'
import VideoRoomMaster from '@/store/models/VideoRoomMaster'

import SelectMemberModal from '@/components/organisms/SelectMemberModal'
import SelectReservationAtByRoomModal from '@/components/organisms/SelectReservationAtByRoomModal'

export default {
  name: 'EditMeetingReservations',
  components: {
    SelectMemberModal,
    SelectReservationAtByRoomModal
  },
  computed: {
    videoRoomMasterId: function() { return this.$route.params.videoRoomMasterId },
    videoRoomMaster: function() { return VideoRoomMaster.find(this.videoRoomMasterId) },
    videoRoomId: function() { return this.$route.params.id },
    videoRoom: function() { return VideoRoom.find(this.videoRoomId) },
    validated: function() {
      return this.editVideoRoom.meetingTitle && this.editVideoRoom.startAt && this.editVideoRoom.endAt
    },
    addedUsers: function() {
      return User.findIn(this.editVideoRoom.userIds)
    },
    ...mapGetters({
      httpError: 'httpError',
      signedInUser: 'auth/signedInUser'
    })
  },
  data() {
    return {
      editVideoRoom: null,
      editVideoRoomInner: {
        meetingTitle: '',
        meetingDescription: '',
        roomPassword: null,
      },
      editMeetingTitleModal: false,
      editReservationAtModal: false,
      editMeetingDescriptionModal: false,
      editRoomPasswordModal: false,
      addVideoRoomMembersModal: false,
      deleteVideoRoomMembersModal: false,
      updateVideoRoomRequesting: false,
      updatedModal: false
    }
  },
  methods: {
    closeEditMeetingTitleModal: function(accept = false) {
      if (accept) {
        this.editVideoRoom.meetingTitle = this.editVideoRoomInner.meetingTitle
      } else {
        this.editVideoRoomInner.meetingTitle = this.editVideoRoom.meetingTitle
      }
      this.editMeetingTitleModal = false
    },
    closeEditMeetingDescriptionModal: function(accept = false) {
      if (accept) {
        this.editVideoRoom.meetingDescription = this.editVideoRoomInner.meetingDescription
      } else {
        this.editVideoRoomInner.meetingDescription = this.editVideoRoom.meetingDescription
      }
      this.editMeetingDescriptionModal = false
    },
    closeEditRoomPasswordModal: function(accept = false) {
      if (accept) {
        this.editVideoRoom.roomPassword = this.editVideoRoomInner.roomPassword
      } else {
        this.editVideoRoomInner.roomPassword = this.editVideoRoom.roomPassword
      }
      this.editRoomPasswordModal = false
    },
    closeUpdatedModal: function() {
      this.$router.push({ name: 'showMeetingReservations', params: { videoRoomMasterId: this.videoRoomMasterId, id: this.videoRoomId } })
    },
    addUsers: function({ userIds, guestUsers }) {
      this.addVideoRoomMembersModal = false
      this.editVideoRoom.userIds = this.editVideoRoom.userIds.concat(userIds)
      this.editVideoRoom.guestUsers = this.editVideoRoom.guestUsers.concat(guestUsers)
    },
    deleteUsers: function({ userIds, guestUsers }) {
      this.deleteVideoRoomMembersModal = false
      this.editVideoRoom.userIds = this.editVideoRoom.userIds.filter((userId) => {
        return !userIds.includes(userId) && !guestUsers.map(guestUser => guestUser.id).includes(userId)
      })
      this.editVideoRoom.guestUsers = this.editVideoRoom.guestUsers.filter((guestUser) => {
        return !guestUsers.map(gu => gu.email).includes(guestUser.email)
      })
    },
    selectedReservationAt: function(reservationAt) {
      const { startAt, endAt } = reservationAt
      this.editVideoRoom.startAt = startAt
      this.editVideoRoom.endAt = endAt
      this.editReservationAtModal = false
    },
    onClickCancel: function() {
      this.$router.go(-1)
    },
    async onClickUpdateVideoRoom() {
      this.updateVideoRoomRequesting = true
      await this.apiPutVideoRoom({
        videoRoomMasterId: this.videoRoomMasterId,
        videoRoomId: this.videoRoomId,
        attributes: this.editVideoRoom
      })
      for(const guestUser of this.editVideoRoom.guestUsers.filter(guestUser => !guestUser.id)) {
        await this.apiPostVideoRoomMember({
          videoRoomMasterId: this.videoRoomMasterId,
          videoRoomId: this.videoRoomId,
          attributes: guestUser
        })
      }
      this.updateVideoRoomRequesting = false

      if (!this.httpError) this.updatedModal = true
    },
    ...mapMutations({
      setForbidden: 'setForbidden'
    }),
    ...mapActions({
      apiGetVideoRoomMaster: 'entities/videoRoomMasters/show',
      apiGetVideoRoom: 'entities/videoRooms/show',
      apiPutVideoRoom: 'entities/videoRooms/update',
      apiPostVideoRoomMember: 'videoRoomMembers/create'
    })
  },
  async created() {
    if (!this.videoRoomMaster) await this.apiGetVideoRoomMaster(this.videoRoomMasterId)
    if (this.videoRoomMaster) {
      await this.apiGetVideoRoom({
        videoRoomMasterId: this.videoRoomMasterId,
        id: this.videoRoomId
      })
    }
    if (!this.videoRoom || this.videoRoomMaster.roomType === 'cafeRoom' || this.videoRoom.inDuration) {
      return
    }

    if (this.videoRoom.roomOwnerId && this.videoRoom.roomOwnerId != this.signedInUser.id) {
      this.setForbidden(true)
      return
    }

    this.editVideoRoom = Object.assign(VideoRoom.skeleton(), {
      meetingTitle: this.videoRoom.meetingTitle,
      meetingDescription: this.videoRoom.meetingDescription,
      roomPassword: this.videoRoom.roomPassword,
      startAt: this.videoRoom.startAt,
      endAt: this.videoRoom.endAt,
      userIds: this.videoRoom.userIds.map(Number),
      guestUsers: this.videoRoom.guestUsers
    })

    this.editVideoRoomInner.meetingTitle = this.editVideoRoom.meetingTitle
    this.editVideoRoomInner.meetingDescription = this.editVideoRoom.meetingDescription
    this.editVideoRoomInner.roomPassword = this.editVideoRoom.roomPassword
  }
}
</script>

<style scoped lang="scss">
@import "@/styles/variables";

.edit-video-room-card {
  display: flex;
  flex-direction: column;
  margin-top: 40px;
  flex-grow: 1;

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  .table-attributes {
    width: 100%;

    tr:not(:last-child) {
      border-bottom: 1px solid $dark-border-color;
    }

    th {
      width: 180px;
      white-space: nowrap;
      padding: 20px 0 20px 20px;
      font-weight: 600;
    }

    tr:last-child th {
      padding: 40px 0 40px 20px;
    }

    td {
      padding: 20px 0;
      img {
        width: 20px;
        height: 20px;
      }

      .attribute-wrapper {
        display: flex;
        cursor: pointer;

        &.invalid .attribute {
          color: $error-color;
        }
      }

      .attribute {
        margin-left: 10px;

        &.empty {
          color: $link-color;
        }
      }
    }
  }

  .title {
    font-weight: 600;
  }

  .room-name span {
    display: flex;
    align-items: center;

    img {
      width: 26px;
      height: 26px;
      margin-right: 10px;
    }
  }

  .prefix-max {
    color: $secondary-color;
    font-weight: 600;
    margin-right: 10px;
  }

  .members {
    flex-grow: 1;
    position: relative;

    .members-scroll-wrapper {
      position: absolute;
      height: 100%;
      width: 100%;
      padding-left: 20px;
      overflow-y: auto;
      display: flex;
      flex-direction: column;

      .member {
        margin-bottom: 10px;

        &:last-child::after {
          content: '';
          display: block;
          height: 20px;
        }
      }
    }
  }

  .edit-video-room-card-footer {
    display: flex;
    justify-content: flex-end;
    padding: 20px;
    border-top: 1px solid $dark-border-color;
  }
}
</style>
